import React from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import { userState } from "../../../../../recoil/auth";
import {
  dashboardDataState,
  selectedAdState,
} from "../../../../../recoil/home/tabs/dashboard/dashboardState";
import { getMemberAdAPI, getMissionActionAPI } from "../../../../../api";

const MyAdsMobile = () => {
  const user = useRecoilValue(userState);
  const [dashboardData, setDashboardData] = useRecoilState(dashboardDataState);
  const [selectedAd, setSelectedAd] = useRecoilState(selectedAdState);
  const isLoggedIn = !!user;

  // 드롭다운 상태 및 옵션
  const [selectedAction, setSelectedAction] = React.useState("전체");
  const [actionOptions, setActionOptions] = React.useState([]);
  const [loadingActions, setLoadingActions] = React.useState(false);

  // 무한 스크롤 상태
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const scrollContainerRef = React.useRef(null);

  // 목데이터 (간단 버전)
  const mockAds = [
    {
      ad_id: 1,
      title: "올려줌 S22 울트라 폰케이스",
      company: "올려줌 공식 스토어",
      imageUrl: "/header_icon.ico",
      statusText: "진행중",
      statusType: "active",
    },
    {
      ad_id: 2,
      title: "올려줌 홍삼정",
      company: "올려줌 공식 스토어",
      imageUrl: "/header_icon.ico",
      statusText: "진행중",
      statusType: "active",
    },
    {
      ad_id: 3,
      title: "올려줌 헤어 신촌점",
      company: "올려줌",
      imageUrl: "/header_icon.ico",
      statusText: "진행중",
      statusType: "active",
    },
  ];

  // 상태 텍스트/타입 변환 (데스크탑과 동일 로직)
  const getStatusText = (status) => {
    const statusMap = {
      PENDING: "승인대기",
      APPROVED: "진행중",
      REJECTED: "거절",
      REFUND: "환불",
      DONE: "종료",
    };
    return statusMap[status] || status;
  };

  const getStatusType = (status) => {
    const typeMap = {
      PENDING: "pending",
      APPROVED: "active",
      REJECTED: "rejected",
      REFUND: "refund",
      DONE: "done",
    };
    return typeMap[status] || "unknown";
  };

  const displayAds = isLoggedIn
    ? (dashboardData?.ads || []).map((apiAd) => ({
        key: apiAd._id,
        title: apiAd.missionName || "광고명",
        company: apiAd.storeName || "",
        imageUrl: apiAd.imageUrl || "/header_icon.ico",
        statusText: getStatusText(apiAd.status),
        statusType: getStatusType(apiAd.status),
        source: apiAd,
      }))
    : mockAds.map((ad) => ({
        key: ad.ad_id,
        title: ad.title,
        company: ad.company,
        imageUrl: ad.imageUrl,
        statusText: ad.statusText,
        statusType: ad.statusType,
        source: ad,
      }));

  // 기본 선택: 0번째 광고 선택
  React.useEffect(() => {
    if (!selectedAd && displayAds.length > 0) {
      setSelectedAd(displayAds[0].source);
    }
  }, [selectedAd, displayAds, setSelectedAd]);

  // 액션 옵션 로드 (로그인 시)
  React.useEffect(() => {
    const loadActions = async () => {
      if (!isLoggedIn) return;
      setLoadingActions(true);
      try {
        const res = await getMissionActionAPI();
        if (res.data?.data) setActionOptions(res.data.data);
      } catch (e) {
        console.error("액션 옵션 로딩 실패", e);
      } finally {
        setLoadingActions(false);
      }
    };
    loadActions();
  }, [isLoggedIn]);

  const fetchAdsByAction = async (actionName) => {
    if (!isLoggedIn || !user?.id) return;
    try {
      let missionTemplate = null;
      if (actionName !== "전체") {
        const found = actionOptions.find((a) => a.name === actionName);
        if (found) missionTemplate = found.ma_id;
      }
      const params = { status: "APPROVED" };
      if (missionTemplate) params.missionTemplate = missionTemplate;
      const response = await getMemberAdAPI(user.id, 1, 6, params);
      if (response.data?.data) {
        setDashboardData((prev) => ({
          ...prev,
          ads: response.data.data,
        }));
        // 새 리스트의 첫번째를 자동 선택
        const first = response.data.data[0];
        if (first) setSelectedAd(first);

        // 페이지 상태 초기화
        setCurrentPage(1);
        setHasMore(
          response.data.pagination
            ? 1 < response.data.pagination.totalPages
            : false
        );
      }
    } catch (e) {
      console.error("액션별 광고 로딩 실패", e);
    }
  };

  // 추가 광고 로딩 (무한 스크롤)
  const loadMoreAds = React.useCallback(async () => {
    if (!isLoggedIn || !user?.id || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;

      let missionTemplate = null;
      if (selectedAction !== "전체") {
        const found = actionOptions.find((a) => a.name === selectedAction);
        if (found) missionTemplate = found.ma_id;
      }

      const params = { status: "APPROVED" };
      if (missionTemplate) params.missionTemplate = missionTemplate;

      const response = await getMemberAdAPI(user.id, nextPage, 6, params);

      if (response.data?.data && response.data.data.length > 0) {
        setDashboardData((prev) => {
          const existingAdIds = new Set(prev.ads.map((ad) => ad._id));
          const newAds = response.data.data.filter(
            (ad) => !existingAdIds.has(ad._id)
          );

          if (newAds.length === 0) {
            setHasMore(false);
            return prev;
          }

          return {
            ...prev,
            ads: [...prev.ads, ...newAds],
          };
        });

        setCurrentPage(nextPage);
        setHasMore(nextPage < (response.data.pagination?.totalPages || 0));
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error("추가 광고 로딩 실패", e);
    } finally {
      setLoadingMore(false);
    }
  }, [
    isLoggedIn,
    user?.id,
    currentPage,
    loadingMore,
    hasMore,
    selectedAction,
    actionOptions,
  ]);

  // 가로 스크롤 감지
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;

      // 90% 스크롤했을 때 다음 페이지 로드
      if (scrollPercentage > 0.9 && hasMore && !loadingMore) {
        loadMoreAds();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, loadMoreAds]);

  return (
    <div className="dashboard-mobile-card">
      <div className="dashboard-mobile-card-header">
        <h3>진행중인 광고</h3>
        {isLoggedIn && (
          <select
            value={selectedAction}
            onChange={async (e) => {
              const value = e.target.value;
              setSelectedAction(value);
              await fetchAdsByAction(value);
            }}
            className="dashboard-mobile-ads-filter"
            disabled={loadingActions}
          >
            <option value="전체">전체</option>
            {actionOptions.map((action) => (
              <option key={action._id} value={action.name}>
                {action.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="myads-mobile-scroll" ref={scrollContainerRef}>
        {displayAds.map((ad, index) => {
          const isSelected = selectedAd
            ? ad.source?._id && selectedAd?._id
              ? selectedAd._id === ad.source._id
              : ad.source?.ad_id && selectedAd?.ad_id
              ? selectedAd.ad_id === ad.source.ad_id
              : false
            : index === 0;
          return (
            <div
              className={`myads-mobile-item ${isSelected ? "selected" : ""}`}
              key={ad.key || index}
              onClick={() => setSelectedAd(ad.source)}
            >
              {ad.imageUrl ? (
                <img
                  src={ad.imageUrl}
                  alt={ad.title}
                  className="myads-mobile-image"
                />
              ) : (
                <div className="myads-mobile-image-placeholder" />
              )}
              <div className="myads-mobile-info">
                <div className="myads-mobile-title">{ad.title}</div>
                <div className="myads-mobile-company">{ad.company}</div>
                {ad.statusType && (
                  <span className={`dashboard-status-badge ${ad.statusType}`}>
                    {ad.statusText}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {isLoggedIn && loadingMore && (
          <div className="myads-mobile-loading">
            <div className="myads-mobile-loading-text">로딩 중...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAdsMobile;
